/**
 * Sevens Layer
 *
 * Implements sevens trump type for marks bids.
 *
 * Rules:
 * - Only available when marks bid won (not a bid type, a trump selection)
 * - Domino closest to 7 total pips wins trick
 * - Ties won by first played
 * - Must play domino closest to 7 total pips (replaces follow-suit)
 * - Must win all tricks (early termination)
 * - Bidder leads first trick
 * - Winner leads next trick (standard Texas 42, handled by core)
 */

import type { Layer } from './types';
import { checkTrickBasedHandOutcome, getPlayerTeam, getDistanceFromSeven, findClosestToSeven, calculateTrickBasedScore } from './helpers';
import { BID_TYPES } from '../constants';

export const sevensLayer: Layer = {
  name: 'sevens',

  getValidActions: (state, prev) => {
    // Add sevens as trump option when marks bid won
    if (state.phase === 'trump_selection' && state.currentBid?.type === 'marks') {
      return [
        ...prev,
        {
          type: 'select-trump',
          player: state.winningBidder,
          trump: { type: 'sevens' }
        }
      ];
    }

    return prev;
  },

  rules: {
    isValidTrump: (_state, trump, prev) => {
      if (trump.type === 'sevens') return true;
      return prev;
    },

    getFirstLeader: (_state, trumpSelector, trump, prev) => {
      if (trump.type !== 'sevens') return prev;
      // Sevens: bidder leads first trick (standard behavior)
      return trumpSelector;
    },

    calculateScore: (state, prev) => {
      if (state.currentBid.type !== BID_TYPES.MARKS || state.trump.type !== 'sevens') {
        return prev;
      }
      return calculateTrickBasedScore(state, true, prev); // mustWin=true: must win ALL tricks
    },

    usesTrickPointsForContract: (state, prev) =>
      state.trump.type === 'sevens' ? false : prev,

    usesConventionalSuitControl: (state, prev) =>
      state.trump.type === 'sevens' ? false : prev,

    // Completely different trick winner algorithm
    calculateTrickWinner: (state, trick, prev) => {
      if (state.trump?.type !== 'sevens') return prev;

      // Distance from 7 total pips
      const distances = trick.map(play => getDistanceFromSeven(play.domino));

      const minDistance = Math.min(...distances);

      // First domino with minimum distance wins (tie = first)
      const winnerIndex = trick.findIndex(play =>
        getDistanceFromSeven(play.domino) === minDistance
      );

      return trick[winnerIndex]?.player ?? prev;
    },

    // Early termination on lost trick
    checkHandOutcome: (state, prev) => {
      if (state.trump?.type !== 'sevens') return prev;
      const biddingTeam = getPlayerTeam(state, state.winningBidder);
      return checkTrickBasedHandOutcome(state, biddingTeam, true);
    },

    // ============================================
    // VALIDATION RULES: Must play closest to 7
    // ============================================

    isValidPlay: (state, domino, playerId, prev) => {
      // If not sevens, use previous Layer's logic
      if (state.trump?.type !== 'sevens') return prev;

      // Basic validation
      if (state.phase !== 'playing') return false;

      const player = state.players[playerId];
      if (!player || !player.hand.some((d) => d.id === domino.id)) return false;

      // Sevens: must play domino closest to 7 total pips
      const distances = player.hand.map(d => getDistanceFromSeven(d));
      const minDistance = Math.min(...distances);
      const dominoDistance = getDistanceFromSeven(domino);

      return dominoDistance === minDistance;
    },

    getValidPlays: (state, playerId, prev) => {
      // If not sevens, use previous Layer's logic
      if (state.trump?.type !== 'sevens') return prev;

      const player = state.players[playerId];
      if (!player || player.hand.length === 0) return [];

      // Sevens: must play domino(es) closest to 7 total pips
      return findClosestToSeven(player.hand);
    }
  }
};
